import React, {
  useState,
  useEffect,
  useMemo,
} from "react";
import rcsLogo from "../assets/rcslogo.png";
import {
  useNavigate,
} from "react-router-dom";

import {
  Wallet,
  Landmark,
  PiggyBank,
  TrendingUp,
  Plus,
  Search,
  ArrowUpRight,
  Trash2,
  Receipt,
  ChevronRight,
} from "lucide-react";

import "../Styles/Accounts.css";

import NewAccountModal from "./NewAccountModal";

import api from "../api";

import { toast } from "react-toastify";

const Accounts = () => {

  const navigate = useNavigate();

  const [accounts, setAccounts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [isModalOpen, setModalOpen] =
    useState(false);

  const [search, setSearch] =
    useState("");

  /* =========================================
      FETCH ACCOUNTS
  ========================================= */

  useEffect(() => {

    const fetchAccounts =
      async () => {

        try {

          setLoading(true);

          const data =
            await api.getAccounts();

          const updatedAccounts =
            await Promise.all(

              data.map(
                async (account) => {

                  const transactions =
                    await api.getTransactions(
                      account.id
                    );

                  const totalDebits =
                    transactions.reduce(
                      (acc, txn) =>
                        acc +
                        (
                          txn.debit
                            ? parseFloat(
                                txn.debit
                              )
                            : 0
                        ),
                      0
                    );

                  const totalCredits =
                    transactions.reduce(
                      (acc, txn) =>
                        acc +
                        (
                          txn.credit
                            ? parseFloat(
                                txn.credit
                              )
                            : 0
                        ),
                      0
                    );

                  const balance =
                    parseFloat(
                      totalDebits -
                      totalCredits
                    );

                  return {
                    ...account,

                    balance,

                    balanceType:
                      balance >= 0
                        ? "Debit"
                        : "Credit",
                  };
                }
              )
            );

          setAccounts(
            updatedAccounts
          );

        } catch (error) {

          console.error(error);

          toast.error(
            "Failed to load accounts."
          );

        } finally {

          setLoading(false);

        }
      };

    fetchAccounts();

  }, []);

  /* =========================================
      FILTERED ACCOUNTS
  ========================================= */

  const filteredAccounts =
    useMemo(() => {

      return accounts.filter(
        (account) =>

          account.name
            .toLowerCase()
            .includes(
              search.toLowerCase()
            )
      );

    }, [accounts, search]);

  /* =========================================
      STATS
  ========================================= */

  const totalAccounts =
    accounts.length;

  const totalAssets =
    accounts
      .filter(
        (a) => a.balance > 0
      )
      .reduce(
        (acc, curr) =>
          acc + curr.balance,
        0
      );

  const totalLiabilities =
    accounts
      .filter(
        (a) => a.balance < 0
      )
      .reduce(
        (acc, curr) =>
          acc + Math.abs(curr.balance),
        0
      );

  const netPosition =
    totalAssets -
    totalLiabilities;

  /* =========================================
      CREATE ACCOUNT
  ========================================= */

  const addAccount =
    async (newAccount) => {

      try {

        const created =
          await api.createAccount(
            newAccount
          );

        setAccounts(
          (prev) => [
            ...prev,
            {
              ...created,
              balance: 0,
              balanceType:
                "Debit",
            },
          ]
        );

        toast.success(
          "Account created!"
        );

      } catch {

        toast.error(
          "Failed to create account."
        );

      }
    };

  /* =========================================
      DELETE ACCOUNT
  ========================================= */

  const deleteAccount =
    async (id) => {

      if (
        !window.confirm(
          "Delete this account?"
        )
      )
        return;

      try {

        await api.deleteAccount(
          id
        );

        setAccounts(
          accounts.filter(
            (a) => a.id !== id
          )
        );

        toast.success(
          "Account deleted."
        );

      } catch {

        toast.error(
          "Delete failed."
        );

      }
    };

  /* =========================================
      LOADING
  ========================================= */
if (loading) {

  return (

    <div className="accounts-loading-screen">

      <div className="accounts-loader-container">

        {/* GLOW */}

        <div className="accounts-loader-glow" />

        {/* SPINNER */}

        <div className="accounts-loader-spinner">

          {/* PRIMARY RING */}

          <div className="accounts-loader-ring" />

          {/* SECONDARY RING */}

          <div className="accounts-loader-ring-secondary" />

          {/* LOGO */}

          <div className="accounts-loader-logo">

            <img
              src={rcsLogo}
              alt="RCS Logo"
            />

          </div>

        </div>

        {/* TEXT */}

        <div className="accounts-loader-text">

          <h2>
            Loading Accounts
          </h2>

          <p>
            Preparing balances,
            ledgers and financial
            records...
          </p>

        </div>

      </div>

    </div>
  );
}
  /* =========================================
      UI
  ========================================= */

  return (

    <div className="accounts-page">

      {/* =====================================
          HERO
      ===================================== */}

      <div className="accounts-hero">

        <div>

          <div className="accounts-pill">

            <Landmark size={15} />

            <span>
              Financial Accounts
            </span>

          </div>

          <h1>
            Accounts Overview
          </h1>

          <p>
            Manage balances,
            ledgers,
            transactions and
            operational
            cashflow across
            the RCS ecosystem.
          </p>

        </div>

        <div className="accounts-hero-actions">

          <button
            className="glass-btn secondary"
            onClick={() =>
              navigate(
                "/journals"
              )
            }
          >
            <Receipt size={18} />
            Journals
          </button>

          <button
            className="glass-btn primary"
            onClick={() =>
              setModalOpen(true)
            }
          >
            <Plus size={18} />
            New Account
          </button>

        </div>

      </div>

      {/* =====================================
          STATS
      ===================================== */}

      <div className="accounts-stats-grid">

        <div className="stat-card">

          <div className="stat-icon green">
            <Wallet size={22} />
          </div>

          <div>

            <span>
              Total Accounts
            </span>

            <h2>
              {totalAccounts}
            </h2>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon blue">
            <TrendingUp size={22} />
          </div>

          <div>

            <span>
              Total Assets
            </span>

            <h2>
              $
              {totalAssets.toLocaleString()}
            </h2>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon orange">
            <PiggyBank size={22} />
          </div>

          <div>

            <span>
              Liabilities
            </span>

            <h2>
              $
              {totalLiabilities.toLocaleString()}
            </h2>

          </div>

        </div>

        <div className="stat-card">

          <div className="stat-icon purple">
            <ArrowUpRight size={22} />
          </div>

          <div>

            <span>
              Net Position
            </span>

            <h2>
              $
              {netPosition.toLocaleString()}
            </h2>

          </div>

        </div>

      </div>

      {/* =====================================
          TABLE SECTION
      ===================================== */}

      <div className="accounts-table-wrapper">

        {/* SEARCH */}

        <div className="table-toolbar">

          <div className="search-box">

            <Search size={18} />

            <input
              type="text"
              placeholder="Search accounts..."
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </div>

        </div>

        {/* TABLE */}

        <div className="accounts-table">

          <div className="table-head">

            <span>
              Account
            </span>

            <span>
              Balance
            </span>

            <span>
              Type
            </span>

            <span>
              Status
            </span>

            <span>
              Actions
            </span>

          </div>

          {filteredAccounts.map(
            (account) => (

              <div
                key={account.id}
                className="table-row"
                onClick={() =>
                  navigate(
                    `/accounts/${account.id}`
                  )
                }
              >

                {/* ACCOUNT */}

                <div className="account-info">

                  <div className="account-avatar">

                    {account.name
                      ?.charAt(0)
                      ?.toUpperCase()}

                  </div>

                  <div>

                    <h4>
                      {account.name}
                    </h4>

                    <p>
                      ID #
                      {account.id}
                    </p>

                  </div>

                </div>

                {/* BALANCE */}

                <div
                  className={
                    account.balance >= 0
                      ? "balance-positive"
                      : "balance-negative"
                  }
                >

                  $
                  {Math.abs(
                    account.balance
                  ).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                    }
                  )}

                </div>

                {/* TYPE */}

                <div>

                  <span className="type-pill">

                    {
                      account.balanceType
                    }

                  </span>

                </div>

                {/* STATUS */}

                <div>

                  <span className="status-pill active">

                    Active

                  </span>

                </div>

{/* ACTIONS */}

<div
  className="row-actions"
  onClick={(e) =>
    e.stopPropagation()
  }
>

  <button
    className="action-btn view-btn"
    onClick={() =>
      navigate(
        `/accounts/${account.id}`
      )
    }
  >
    <ChevronRight size={16} />
    Open
  </button>

  <button
    className="action-btn delete-btn-modern"
    onClick={() =>
      deleteAccount(account.id)
    }
  >
    <Trash2 size={15} />
    Delete
  </button>

</div>

              </div>
            )
          )}

        </div>

      </div>

      {/* =====================================
          MODAL
      ===================================== */}

      {isModalOpen && (

        <NewAccountModal
          closeModal={() =>
            setModalOpen(false)
          }
          addAccount={
            addAccount
          }
        />

      )}

    </div>
  );
};

export default Accounts;